import React, { useEffect, useState } from 'react';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import {
  SimpleFormSingleDropdown,
  SimpleFormTextField,
} from '../../shared/form/Fields';
import { withLocalePrefix } from '../../../shared/utils/translation';
import {
  RejectionType,
  RejectionOption,
  RejectionReasonsOptionsResponse,
  BirchDatePickerFormat,
} from '../../shared/types';
import CustomFieldsFormFields from './custom_fields_form_fields';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { RejectionCategory } from './reject_modal';
const t = withLocalePrefix('candidate_profile_redesign.modals.reject');

interface CustomReasonProps {
  customRejectionTypeOptions: RejectionOption[];
  setRejectionTypeOnChange: (item: RejectionOption) => void;
  onClick: () => void;
  required: boolean;
}

interface ExistingReasonProps {
  canCreateNewReason: boolean;
  rejectionOptions: RejectionOption[];
  setRejectionTypeOnChange: (item: RejectionOption) => void;
  onClick: () => void;
  required: boolean;
  spamReason: { label: string; value: string };
}
const CustomReason = ({
  customRejectionTypeOptions,
  setRejectionTypeOnChange,
  onClick,
  required,
}: CustomReasonProps) => {
  return (
    <Stack>
      <SimpleFormSingleDropdown
        name="custom_rejection_type"
        label={t('type')}
        items={customRejectionTypeOptions}
        onSelectedItemChange={setRejectionTypeOnChange}
        rules={{ required: required ? t('rejection_type_required') : false }}
      />
      <Stack spacing={0}>
        <SimpleFormTextField
          name="custom_rejection_reason"
          label={t('rejection_reason')}
          data-provides="custom-rejection-reason"
          rules={{ required: required ? t('reason_required') : false }}
        />
        <TertiaryButton size="md" onClick={onClick}>
          {t('use_existing_reason')}
        </TertiaryButton>
      </Stack>
    </Stack>
  );
};

const ExistingReason = ({
  canCreateNewReason,
  rejectionOptions,
  setRejectionTypeOnChange,
  onClick,
  required,
  spamReason,
}: ExistingReasonProps) => {
  const [showSpamWarning, setShowSpamWarning] = useState(false);
  const { watch } = useFormContext();
  const rejectionCategory = watch('rejection_category') as RejectionCategory;
  const spamReasonId = spamReason?.value;

  const rejectionReason = watch('rejection_reason');
  useEffect(() => {
    if (rejectionCategory === 'security_risk') {
      setShowSpamWarning(true);
    } else if (rejectionReason?.value === spamReasonId) {
      setShowSpamWarning(true);
    } else {
      setShowSpamWarning(false);
    }
  }, [rejectionReason, spamReasonId, rejectionCategory]);

  return (
    <Stack spacing={0}>
      <SimpleFormSingleDropdown
        name="rejection_reason"
        label={t('rejection_reason')}
        items={rejectionOptions}
        onSelectedItemChange={setRejectionTypeOnChange}
        rules={{ required: required ? t('reason_required') : false }}
        helperText={showSpamWarning && t('spam_warning')}
      />
      {canCreateNewReason && (
        <TertiaryButton
          size="md"
          onClick={onClick}
          data-provides="create-new-rejection-reason"
        >
          {t('create_a_new_reason')}
        </TertiaryButton>
      )}
    </Stack>
  );
};

const RejectionReasonFields = ({
  canCreateNewReason,
  rejectionOptions,
  typeOptions,
  customFieldsOptions,
  currencyOptions,
  required,
  spamReason,
  dateFormatBirchPicker,
  userOptionsPath,
  stageTypes,
  hideCreateNewReason = false,
}: RejectionReasonsOptionsResponse & {
  stageTypes: string[];
  dateFormatBirchPicker: BirchDatePickerFormat;
  userOptionsPath: string;
  hideCreateNewReason?: boolean;
}) => {
  const { setValue, getValues, watch } = useFormContext();

  const [createReason, setCreateReason] = useState(false);
  const [rejectionReasonType, setRejectionReasonType] =
    useState<RejectionType | null>(getValues('rejection_reason')?.type);

  const rejectionCategory = watch('rejection_category');
  const rejectionReason = watch('rejection_reason');

  useEffect(() => {
    setCreateReason(false);
    setRejectionReasonType(null);
  }, [rejectionCategory]);

  useEffect(() => {
    setRejectionReasonType(rejectionReason?.type || null);
  }, [rejectionReason]);

  const setRejectionTypeOnChange = (item: RejectionOption) => {
    setRejectionReasonType(item?.type || null);
  };

  return (
    <>
      <Stack>
        {createReason ? (
          <CustomReason
            customRejectionTypeOptions={typeOptions}
            setRejectionTypeOnChange={setRejectionTypeOnChange}
            onClick={() => {
              setValue('rejection_reason', null);
              setRejectionReasonType(null);
              setCreateReason(false);
            }}
            required={createReason && required}
          />
        ) : (
          <ExistingReason
            canCreateNewReason={canCreateNewReason && !hideCreateNewReason}
            rejectionOptions={rejectionOptions}
            spamReason={spamReason}
            setRejectionTypeOnChange={setRejectionTypeOnChange}
            onClick={() => {
              setValue('custom_rejection_type', null);
              setValue('custom_rejection_reason', null);
              setRejectionReasonType(null);
              setCreateReason(true);
            }}
            required={required}
          />
        )}
      </Stack>

      {!hideCreateNewReason && (
        <CustomFieldsFormFields
          rejectionReasonType={rejectionReasonType}
          customFields={customFieldsOptions}
          currencyOptions={currencyOptions}
          stageTypes={stageTypes}
          dateFormatBirchPicker={dateFormatBirchPicker}
          userOptionsPath={userOptionsPath}
        />
      )}
    </>
  );
};

export default RejectionReasonFields;
